<script lang="ts" setup>
	export type FrameClass = {
		base: string
		overflow: string
		img: Partial<{
			base: string
			width: string
			height: string
		}>
		imgWrapper: Partial<{
			base: string
			height: string
			width: string
			position: string
			rounded: string
		}>
	}

	const configClass: FrameClass = {
		base: '',
		overflow: '',
		img: {
			base: 'object-cover',
			width: '',
			height: 'md:max-h-[656px] max-h-[400px]'
		},
		imgWrapper: {
			height: 'md:min-h-[541px] h-full',
			width: 'w-full sm:min-w-[456px] ',
			position: 'relative',
			rounded: 'rounded '
		}
	}

	const { img, imgWrapper, base, overflow } = configClass
	defineProps<{
		src: string
		alt?: string
		ui?: Partial<FrameClass>
	}>()

	const careList = [
		{
			icon: 'hospital-circle',
			label: 'Premium hospitals'
		},
		{
			icon: 'chronic-care',
			label: 'Chronic care'
		},
		{
			icon: 'intensive-care',
			label: 'Intensive care'
		},
		{
			icon: 'global-coverage',
			label: 'Global coverage'
		}
	]
</script>
<template>
	<div :class="[ui?.base ?? base, ui?.overflow ?? overflow, 'relative']">
		<ul
			class="absolute bottom-5 md:bottom-0 max-h-[220px] md:top-0 md:my-auto pointer-events-none pt-1 overflow-hidden wrap custom-transition bg- rounded-[6px] lg:-left-[2rem] inline-flex h-auto flex-col z-10"
		>
			<li
				class="flex list-effect items-center justify-between rounded-t-[6px] h-9 md:h-[56px] bg-white gap-x-5 w-full px-2"
			>
				<div class="flex items-center gap-x-2">
					<tools-icon
						:icon="careList[0].icon"
						dir="random"
						:height="18"
						:width="18"
						svg-class="sm:w-[28px] sm:h-[28px] w-[16px] h-[16px]"
					/>
					<span class="xs:text-xs text-[10px] font-medium text-health-900">{{ careList[0].label }}</span>
				</div>

				<div class="flex items-center gap-x-3 flex-none">
					<div
						class="min-w-[36px] bg-health-100 rounded-[20px] relative h-1 overflow-hidden progress-wrapper"
					>
						<div
							class="progress absolute bg-health-500 top-0 bottom-0 my-auto h-full w-[20px] rounded-[inherit] custom-transition-2"
						/>
					</div>

					<div
						class="flex-none max-w-[40px] overflow-hidden min-h-[30px] min-w-[20px] px-2 relative inline-flex justify-end w-full items-end flex-col"
					>
						<slot name="right-icon">
							<span
								class="animate-icon absolute left-3 bottom-1 h-full"
								:style="{ '--star-delay': `${2 * 3.2}s` }"
							>
								<tools-icon dir="random" icon="star-rainbow" />
							</span>
						</slot>
					</div>
				</div>
			</li>

			<li
				v-for="(item, index) in careList.length - 1"
				:key="index"
				class="flex list-effect overflow-hidden h-0 items-center last:rounded-b-[6px] justify-between gap-x-5 w-full px-2 absolute active bg-white"
				:style="{ '--animate-height-duration': `${(index + 1) * 2}s` }"
			>
				<div class="flex items-center gap-x-2">
					<tools-icon
						:icon="careList[index + 1].icon"
						dir="random"
						:height="18"
						:width="18"
						svg-class="sm:w-[28px] sm:h-[28px] w-[16px] h-[16px]"
					/>
					<span class="xs:text-xs text-[10px] font-medium text-health-900">{{
						careList[index + 1].label
					}}</span>
				</div>

				<div class="flex items-center gap-x-3 flex-none">
					<div
						class="min-w-[36px] bg-health-100 rounded-[20px] relative h-1 overflow-hidden progress-wrapper"
					>
						<div
							class="progress absolute bg-health-500 top-0 bottom-0 my-auto h-full w-[5px] rounded-[inherit] custom-transition-2"
							:style="{ '--animate-width-duration': `${(index + 2) * 3}s` }"
						/>
					</div>

					<div
						class="flex-none max-w-[40px] overflow-hidden min-h-[30px] min-w-[20px] px-2 relative inline-flex justify-end w-full items-end flex-col"
					>
						<slot name="right-icon">
							<span
								class="animate-icon absolute left-3 bottom-1 h-full"
								:style="{ '--star-delay': `${(index + 2) * 3.2}s` }"
							>
								<tools-icon dir="random" icon="star-rainbow" />
							</span>
						</slot>
					</div>
				</div>
			</li>
		</ul>

		<div
			:class="[
				ui?.imgWrapper?.base ?? imgWrapper.base,
				ui?.imgWrapper?.height ?? imgWrapper.height,
				ui?.imgWrapper?.position ?? imgWrapper.position,
				ui?.imgWrapper?.rounded ?? imgWrapper.rounded,
				ui?.imgWrapper?.width ?? imgWrapper.width
			]"
		>
			<nuxt-img
				loading="lazy"
				:src="src"
				:alt="alt || ''"
				:class="[ui?.img?.base ?? img.base, ui?.img?.height ?? img.height, ui?.img?.width ?? img.width]"
				preload
			/>
		</div>
	</div>
</template>

<style scoped>
	:root {
		--animate-height-duration: 0;
		--animate-width-duration: 1s;
		--star-delay: 0s;
	}
	.list-effect.active {
		animation: heighten 2s linear forwards;
		animation-delay: var(--animate-height-duration);
		transition: 800ms ease-in-out;
		transform-origin: top;
		transform-style: preserve-3d;
		@apply h-[0] relative;
	}
	.progress {
		animation: width 1s linear forwards;
		animation-delay: var(--animate-width-duration);
		transition: 800ms linear;
	}
	.animate-icon {
		transform-origin: center;
		transform: scale(1);
		animation: scale-up 3s ease-out infinite 3s alternate;
		animation-delay: var(--star-delay);
	}

	@keyframes scale-up {
		from {
			transform: scale(1) rotate(0);
		}
		to {
			transform: scale(2) rotate(6deg);
		}
	}
	@keyframes width {
		0% {
			width: 10%;
		}
		100% {
			width: 100%;
		}
	}

	@keyframes heighten {
		0% {
			height: 0;
		}
		100% {
			height: 36px;
		}
	}

	@media screen and (min-width: 768px) {
		@keyframes heighten {
			0% {
				height: 0;
			}
			100% {
				height: 56px;
			}
		}
	}
</style>
